 "use client";

import { IResource, ISchedule, ScheduleParamsAll } from "@gettactic/api";
import { getStorageFloor } from "@gettactic/helpers/src/resources/getStorageFloor";
import { getScheduleUser } from "@gettactic/helpers/src/schedules/getScheduleUser";
import { selectFloor } from "@gettactic/helpers/src/resources/selectFloor";
import { fillSchedules } from "@gettactic/helpers/src/schedules/fillSchedules";
import { formatApiDatetime } from "@gettactic/helpers/src/date-time/formatApiDatetime";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";
import { useAuthenticated } from "../authenticated/useAuthenticated";
import { useMapSchedulable } from "../schedules/useMapSchedulable";
import { useUserListPaginated } from "../users/useUserListPaginated";
import {
  IFloorWithResources,
  IResourceWithSchedules,
  IUserWithResource
} from "./types";
import { useFloors } from "./useFloors";
import { useResources } from "./useResources";

export interface IReturn {
  loading: boolean;
  schedulable: IResourceWithSchedules[] | undefined;
  floorsWithResources: IFloorWithResources[] | undefined;
  floors: IResource[] | undefined;
  floor: IResource | null;
  setFloor: Dispatch<SetStateAction<IResource | null>>;
  usersAtOffice: IUserWithResource[] | undefined;
  resources: IResource[] | undefined;
  reload: () => Promise<void>;
}

export interface UseOfficeResourcesOptions {
  officeId: string | null;
  startDate: Date | null;
  endDate: Date | null;
  filterQuery: string;
  entireOffice: boolean;
  getAllMaps?: boolean;
  _lastFloorId?: string | null;
  coreApi?: boolean;
  tags?: string[];
  tags_operator?: "AND" | "OR";
  floorId?: string | null;
  initialFloorId?: string | null;
  allowNoFloor?: boolean;
  /** Pre-formatted start datetime string — bypasses Date→format conversion */
  rawStartTime?: string | null;
  /** Pre-formatted end datetime string — bypasses Date→format conversion */
  rawEndTime?: string | null;
}

export function useOfficeResources(
  options: UseOfficeResourcesOptions
): IReturn {
  const {
    officeId,
    startDate,
    endDate,
    filterQuery,
    entireOffice,
    getAllMaps,
    _lastFloorId,
    tags,
    tags_operator,
    floorId
  } = options;
  const { api } = useTacticHooks();
  const {
    userContext: { currentOffice: contextSelectedOffice, authenticatedUser }
  } = useAuthenticated();

  const currentOffice =
    (officeId ? authenticatedUser?.offices?.byId?.[officeId] : null) ||
    contextSelectedOffice;

  const [initialFloorId, setInitialFloorId] = useState<
    string | null | undefined
  >(options.initialFloorId);

  // Sync initialFloorId when options.initialFloorId changes (e.g., from URL navigation)
  useEffect(() => {
    if (options.initialFloorId && options.initialFloorId !== initialFloorId) {
      setInitialFloorId(options.initialFloorId);
    }
  }, [options.initialFloorId]);

  const startDateWithTimeStr = options.rawStartTime
    ? `${options.rawStartTime}Z`
    : startDate
      ? formatApiDatetime(startDate)
      : null;
  const ebdDateWithTimeStr = options.rawEndTime
    ? `${options.rawEndTime}Z`
    : endDate
      ? formatApiDatetime(endDate)
      : null;
  const startDateStr = options.rawStartTime
    ? options.rawStartTime.split('T')[0]
    : startDate ? format(startDate, "yyyy-MM-dd") : null;

  let loading: boolean;
  const paginatedUsers = useUserListPaginated(filterQuery, 200);

  loading = !paginatedUsers.data;

  const { floors: allFloors } = useFloors(officeId, null);
  const [floor, setFloor] = useState<IResource | null>(null);

  const loadFloor = () => {
    if (!allFloors) {
      return;
    }

    const lastFloorId = _lastFloorId || getStorageFloor();

    const result = selectFloor({
      allFloors,
      getAllMaps,
      initialFloorId: initialFloorId || undefined,
      controlledFloorId: floorId,
      lastFloorId,
      defaultAreaId: authenticatedUser.defaultAreaId,
      currentFloorId: floor?.id,
      allowNoFloor: options?.allowNoFloor,
    });

    const newFloor = result.floor;

    if (
      floor?.id !== newFloor?.id ||
      floor?.map_data !==
        newFloor?.map_data /* This is to allow map view refresh after saving a map in map builder */
    ) {
      if (initialFloorId) setInitialFloorId(null);
      setFloor(newFloor);
    }
  };

  // Try to use the last used floor,
  // then the default profile floor,
  // then selected floor, finally the first floor in the array
  // Note: Only depend on authenticatedUser.defaultAreaId, not the whole object,
  // to prevent re-running when unrelated user data changes
  useEffect(loadFloor, [
    allFloors,
    floor?.id,
    authenticatedUser.defaultAreaId,
    currentOffice.id,
    floorId,
    initialFloorId
  ]);

  loading ||= typeof allFloors === "undefined";

  // We should be sure that during office switch we
  // have the floors updated data to avoid doing extra requests
  const floorExists = !!allFloors?.find(
    (x) => x.id === floor?.id && floor.office_id === currentOffice.id
  );
  const enableFetch = floorExists;

  const params: ScheduleParamsAll = {
    office: officeId ? [officeId] : [],
    office_id: officeId || "",
    date: startDateStr ?? "",
    type: ["desk", "workspace", "meeting_room", "parking_lot", "parking_space"],
    tz: currentOffice.time_zone,
    days: 1,
    resource: !entireOffice && floor?.id ? [floor?.id] : undefined
  };
  const { data: usersSchedule, refetch: refetchSchedules } = useQuery({
    queryKey: ["schedules", "occupied", params],

    queryFn: async () => {
      const res = await api.client.schedules.allIndexed(
        "occupied",
        params,
        options.coreApi || false
      );
      return res.result;
    },

    enabled: enableFetch && officeId !== null
  });

  loading ||= floorExists && !usersSchedule;

  const floorsWithMap = allFloors
    ? getAllMaps
      ? allFloors
      : allFloors.filter((x) => x.map_url || x.map_data)
    : undefined;

  const { resources, refetch: refetchResources } = useResources(
    {
      officeId,
      types: [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space"
      ],
      parentId: !entireOffice ? floor?.id : undefined,
      assignmentsDate: startDateStr
    },
    {
      enabled: enableFetch && officeId !== null
    }
  );

  loading ||= floorExists && !resources;

  const {
    schedulable,
    error: schedulableError,
    refetch: refetchMApSchedulable
  } = useMapSchedulable({
    officeId,
    parentId: !entireOffice ? (floor?.id ?? null) : null,
    startDate: startDateWithTimeStr,
    endDate: ebdDateWithTimeStr,
    enabled: enableFetch,
    tags,
    tags_operator
  });

  const reload = async () => {
    await refetchSchedules();
    await refetchMApSchedulable();
    await refetchResources();
    loadFloor();
  };

  loading ||= floorExists && !schedulable;

  // this merge allows us to have
  // resources with real slot information
  // and filled with the list of users
  // dates specified
  const resourcesFilled = useMemo(() => {
    if (!resources || !schedulable || !startDateStr) {
      return [];
    }
    return fillSchedules(
      resources.elements.map((r: IResource) => {
        // lookup them in schedulable
        const s = (schedulable || []).find((x) => x.id === r.id);
        if (s) {
          return { ...r, ...s, label: r.label, description: r.description };
        }
        // A resource absent from `schedulable` normally means it genuinely
        // isn't bookable that day, so it's shown as unavailable. But when
        // the schedulable fetch itself failed (schedulableError), every
        // resource is "absent" — forcing available_slots to 0 here would
        // make the whole office read as unavailable instead of just
        // reflecting stale (last-known) availability until the retry
        // succeeds.
        return schedulableError
          ? r
          : { ...r, available_slots: 0, is_available: 0 };
      }) as IResource[],
      usersSchedule,
      startDateStr
    );
  }, [resources, schedulable, schedulableError, usersSchedule, startDateStr]);

  const floorsWithResources: IFloorWithResources[] | undefined = useMemo(() => {
    if (!resourcesFilled) {
      return undefined;
    }
    const list = floorsWithMap ?? [];
    const hasOnlyOneFloor = list.length === 1;
    return list.map((floor, number) => ({
      ...floor,
      number: number + 1,
      resources: resourcesFilled
        // get resources of this floor
        .filter((r) => (hasOnlyOneFloor ? true : r.parent_id === floor.id))
    }));
  }, [floorsWithMap, resourcesFilled, schedulable]);

  const usersAtOffice = useMemo(() => {
    if (!resourcesFilled) {
      return undefined;
    }
    return resourcesFilled.reduce((arr, res) => {
      const users = (res.schedules || []).flatMap((sc: ISchedule) => {
        const user = getScheduleUser(sc);
        if (!user) return [];
        return [{ ...user, resourceId: sc.resource.id }];
      });
      return arr.concat(users);
    }, [] as IUserWithResource[]);
  }, [resourcesFilled]);

  return {
    loading,
    // use the schedulable data filled with
    // resources in use
    schedulable: resourcesFilled,
    floors: floorsWithMap,
    floorsWithResources,
    floor,
    setFloor,
    usersAtOffice,
    resources: resources ? resources.elements : undefined,
    reload
  };
}
